import { History } from "lucide-react";
import { fmtMXN, EXPENSE_CATEGORIES, ADMIN_EXPENSE_CATEGORIES } from "@/lib/api";

const CATEGORY_LABEL = {
  ...Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.value, c.label])),
  ...Object.fromEntries(ADMIN_EXPENSE_CATEGORIES.map((c) => [c.value, c.label])),
};

const ROLE_LABEL = {
  admin: "Admin",
  contador: "Contador",
  supervisor: "Supervisor",
  residente: "Residente",
  auxiliar: "Auxiliar",
};

const STATUS_LABEL = {
  pendiente: "Pendiente",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
  pagado: "Pagado",
};

const FUNDED_BY_LABEL = { empresa: "Empresa", persona: "Persona" };

function formatValue(field, value) {
  if (value == null || value === "") return <span className="text-slate-500 italic">vacío</span>;
  if (field === "amount") return <span className="font-mono">{fmtMXN(value)}</span>;
  if (field === "status") return <span className="font-mono">{STATUS_LABEL[value] || value}</span>;
  if (field === "funded_by") return <span className="font-mono">{FUNDED_BY_LABEL[value] || value}</span>;
  if (field === "is_recurring") return <span className="font-mono">{value ? "Sí" : "No"}</span>;
  if (field === "category") return <span className="font-mono">{CATEGORY_LABEL[value] || value}</span>;
  if (typeof value === "boolean") return value ? "Sí" : "No";
  const s = String(value);
  return s.length > 60 ? <span title={s}>{s.slice(0, 60)}…</span> : s;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ChangeHistory({ history }) {
  if (!history || history.length === 0) {
    return (
      <div className="text-[11px] text-slate-500 italic px-2 py-2">
        Sin cambios registrados todavía.
      </div>
    );
  }
  // Render most recent first
  const sorted = [...history].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return (
    <div className="space-y-2" data-testid="change-history">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-3.5 h-3.5" style={{ color: "var(--magenta)" }} />
        <span className="label-mono text-[10px]" style={{ color: "var(--magenta)" }}>
          // HISTORIAL DE CAMBIOS ({sorted.length})
        </span>
      </div>
      {sorted.map((entry, i) => (
        <div
          key={i}
          className="px-3 py-2 rounded-sm text-[11px]"
          style={{ background: "rgba(217,70,239,0.04)", border: "1px solid rgba(217,70,239,0.15)", borderLeft: "3px solid var(--magenta)" }}
        >
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-slate-200">
              {entry.by_name || "—"}
              {entry.by_role && (
                <span className="ml-1.5 label-mono text-[9px] px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(34,211,238,0.1)", color: "var(--cyan)" }}>
                  {ROLE_LABEL[entry.by_role] || entry.by_role}
                </span>
              )}
            </span>
            <span className="label-mono text-[9px] text-slate-500">{formatDateTime(entry.at)}</span>
          </div>
          <div className="space-y-0.5">
            {(entry.changes || []).map((c, j) => (
              <div key={j} className="text-slate-300">
                <span className="text-slate-400">{c.field_label || c.field}:</span>{" "}
                <span className="text-slate-500 line-through">{formatValue(c.field, c.old)}</span>
                <span className="mx-1 text-slate-600">→</span>
                <span className="text-slate-100 font-semibold">{formatValue(c.field, c.new)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
